import React, { useEffect, useState } from "react";
import { Card, Button, Table, Popconfirm, Space } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  DeleteOutlined,
  BarsOutlined,
  BarcodeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  getProductCategories,
  deleteProductCategory,
} from "../../../network/network";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import DownloadImportTemplate from "../../../components/DownloadImportTemplate";
import ImportItems from "../../../components/ImportItems";
import AddCategory from "./AddCategory";
import CategoryFormModal from "./CategoryFormModal";
import { handleApiError } from "../../../utils/errorHandler";

const ProductCategories = () => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [productCategoriesTableData, setProductCategoriesTableData] = useState(
    [],
  );
  const [editCategory, setEditCategory] = useState(null);
  const { t } = useTranslation();

  const reloadCategories = () => {
    setReloadTableData((prevState) => !prevState);
  };

  useEffect(() => {
    setIsDataLoading(true);
    getProductCategories()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setProductCategoriesTableData(
          response?.data?.data?.product_categories || [],
        );
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, t]);

  const handleDelete = (id) => {
    deleteProductCategory(id)
      .then((response) => {
        const data = response?.data || response?.response?.data;

        if (data?.success === true) {
          reloadCategories();
          NotificationWithIcon(
            "success",
            t("category_deleted"),
            t("category_deleted_successfully"),
          );
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "/products",
      breadcrumbName: t("products"),
      icon: <BarcodeOutlined />,
    },
    {
      path: "categories",
      breadcrumbName: t("categories"),
      icon: <BarsOutlined />,
    },
  ];

  const detailsPartColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <p>{index + 1}</p>;
      },
    },
    {
      title: t("name_en"),
      render: (record) => {
        return <>{record.name}</>;
      },
    },
    {
      title: t("name_ar"),
      render: (record) => {
        return <>{record.name_ar}</>;
      },
    },
  ];

  const actionPartColumn = [
    {
      title: t("action"),
      width: "100px",
      align: "center",
      render: (record) => {
        return (
          <Space>
            <Button
              type="link"
              onClick={() => setEditCategory(record)}
              title={t("category_edit")}
            >
              <EditOutlined />
            </Button>

            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => handleDelete(record.id)}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("categories")}
          breadcrumb={CustomBreadcrumb}
          ghost={false}
          extra={[
            <AddCategory
              key="open-add-category-modal"
              iconOnly={false}
              reloadCallback={reloadCategories}
            />,
            <DownloadImportTemplate type="category" key="import-template" />,
            <ImportItems
              type="category"
              callbackLoading={reloadCategories}
              key="import-items"
            />,
          ]}
        />
      </Card>

      <Card>
        <Table
          columns={[...detailsPartColumns, ...actionPartColumn]}
          dataSource={productCategoriesTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>

      <CategoryFormModal
        open={Boolean(editCategory)}
        mode="edit"
        initialValues={editCategory}
        onClose={() => setEditCategory(null)}
        onSuccess={reloadCategories}
      />
    </>
  );
};

export default ProductCategories;
